import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { DataTable, type Column, type SortState } from '@/components/ui/DataTable';

import { SAMPLE_ROWS, type SampleRow } from '../sampleData';
import { State } from '../State';

const COLUMNS: Column<SampleRow>[] = [
  { key: 'name', header: 'Name', render: (row) => row.name },
  { key: 'status', header: 'Status', render: (row) => row.status },
  {
    key: 'supporters',
    header: 'Supporters',
    align: 'right',
    render: (row) => row.supporters.toLocaleString(),
  },
];

type SampleSortKey = 'name' | 'supporters';

const SORTABLE: Column<SampleRow, SampleSortKey>[] = [
  { key: 'name', header: 'Name', render: (row) => row.name, sortKey: 'name' },
  { key: 'status', header: 'Status', render: (row) => row.status },
  {
    key: 'supporters',
    header: 'Supporters',
    align: 'right',
    render: (row) => row.supporters.toLocaleString(),
    sortKey: 'supporters',
  },
];

/** The real pages sort on the server; here six local rows stand in for it. */
function Sortable() {
  const [sort, setSort] = useState<SortState<SampleSortKey>>({
    column: 'name',
    direction: 'asc',
  });

  const rows = [...SAMPLE_ROWS].sort((a, b) => {
    const order =
      sort.column === 'name' ? a.name.localeCompare(b.name) : a.supporters - b.supporters;
    return sort.direction === 'asc' ? order : -order;
  });

  return (
    <DataTable
      caption="Sample campaigns, sortable"
      columns={SORTABLE}
      rows={rows}
      getRowKey={(row) => row.id}
      sort={sort}
      onSortChange={(column) =>
        setSort((current) => ({
          column,
          direction:
            current.column === column && current.direction === 'asc' ? 'desc' : 'asc',
        }))
      }
    />
  );
}

/** The real pages page on the server; here the six rows are sliced locally. */
function Paged() {
  const [page, setPage] = useState(1);
  const pageSize = 3;

  return (
    <DataTable
      caption="Sample campaigns, paged"
      columns={COLUMNS}
      rows={SAMPLE_ROWS.slice((page - 1) * pageSize, page * pageSize)}
      getRowKey={(row) => row.id}
      page={page}
      pageSize={pageSize}
      total={SAMPLE_ROWS.length}
      onPageChange={setPage}
    />
  );
}

export function DataTableExamples() {
  return (
    <>
      <State title="Loaded">
        <DataTable
          caption="Sample campaigns"
          columns={COLUMNS}
          rows={SAMPLE_ROWS.slice(0, 3)}
          getRowKey={(row) => row.id}
        />
      </State>

      <State title="Loading" note="Placeholder rows the size of a page, marked busy.">
        <DataTable
          caption="Sample campaigns, loading"
          columns={COLUMNS}
          rows={[]}
          getRowKey={(row) => row.id}
          loading
          loadingRows={3}
        />
      </State>

      <State title="Empty" note="Genuinely nothing to list.">
        <DataTable
          caption="Sample campaigns, empty"
          columns={COLUMNS}
          rows={[]}
          getRowKey={(row) => row.id}
          emptyMessage="No campaigns yet."
        />
      </State>

      <State title="Nothing matched" note="Different words: filters excluded everything.">
        <DataTable
          caption="Sample campaigns, no match"
          columns={COLUMNS}
          rows={[]}
          getRowKey={(row) => row.id}
          filtered
          noMatchMessage="Nothing matched your search."
        />
      </State>

      <State
        title="Failed"
        note="The retry here reloads nothing; it is the control itself."
      >
        <DataTable
          caption="Sample campaigns, failed"
          columns={COLUMNS}
          rows={[]}
          getRowKey={(row) => row.id}
          error="The campaigns could not be loaded."
          onRetry={() => undefined}
        />
      </State>

      <State title="Sortable">
        <Sortable />
      </State>

      <State title="Paged">
        <Paged />
        <Typography variant="caption" color="text.secondary">
          Three of six rows a page.
        </Typography>
      </State>
    </>
  );
}
